import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { TransactionsTableProps, TransactionV2 } from '../../../types/dashboardV2';
import {
  getTransactionStatusBadgeClass,
  getTransactionStatusDotClass,
  formatTransactionStatus,
} from '../../../shared/utils/statusUtils';
import {
  DataTable,
  type DataTableColumnMeta,
  type DataTableRenderers,
} from '../components/DataTable';
import { StatusBadgeCell, DateTimeCell, IdWithCopyCell, TableActionButton } from '../components/cells';
import { usePrivacy } from '../../../context/PrivacyContext';
import { MASK } from '../../../shared/utils/mask';

/** Column shares below are shares of this; narrower viewports scroll the table instead. */
export const TRANSACTIONS_TABLE_MIN_WIDTH = '1140px';

export const TRANSACTIONS_COLUMNS = [
  { key: 'tranId', labelKey: 'dashboard.tables.transactions.tranId', width: '17%', cellClassName: 'cell-id', skeletonVariant: 'id' },
  { key: 'userId', labelKey: 'dashboard.tables.transactions.userId', width: '21.5%', cellClassName: 'cell-address', skeletonVariant: 'address' },
  { key: 'amount', labelKey: 'dashboard.tables.transactions.amount', width: '13%', align: 'center', cellClassName: 'cell-price', tabularNums: true, skeletonVariant: 'price' },
  { key: 'bank', labelKey: 'dashboard.tables.transactions.bank', width: '12.5%', cellClassName: 'cell-option', skeletonVariant: 'option' },
  { key: 'status', labelKey: 'dashboard.tables.transactions.status', width: '15.5%', skeletonVariant: 'status' },
  { key: 'createdAt', labelKey: 'dashboard.tables.transactions.createdAt', width: '13%', align: 'right', skeletonVariant: 'id' },
  { key: 'actions', labelKey: 'dashboard.tables.actions', width: '7.5%', align: 'center', skeletonVariant: 'action' },
] as const satisfies readonly DataTableColumnMeta[];

export const TransactionsTable = memo(function TransactionsTable({
  transactions,
  total,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onViewDetails,
}: TransactionsTableProps) {
  const { t } = useTranslation();

  const { hidden } = usePrivacy('transactions');

  const renderers = useMemo<DataTableRenderers<TransactionV2, typeof TRANSACTIONS_COLUMNS>>(
    () => ({
      tranId: (tx) => tx.tranId || '-',
      userId: (tx) => (
        <IdWithCopyCell value={tx.userId} copyLabel={t('dashboard.tables.sessions.copyUserId')} />
      ),
      amount: (tx) => (hidden ? MASK : `${tx.amount.toFixed(2)} ₴`),
      bank: (tx) => tx.bank || '-',
      status: (tx) => (
        <StatusBadgeCell
          badgeClass={getTransactionStatusBadgeClass(tx.status)}
          dotClass={getTransactionStatusDotClass(tx.status)}
          label={formatTransactionStatus(tx.status)}
        />
      ),
      createdAt: (tx) => <DateTimeCell value={tx.createdAt} />,
      actions: (tx) => {
        const hasInvoice = Boolean(tx.invoiceId);
        const detailsTitle = hasInvoice
          ? t('dashboard.tables.transactions.viewDetailsAria', { id: tx.tranId || '—' })
          : t('dashboard.tables.transactions.noInvoiceAria');
        return (
          <TableActionButton
            icon="visibility"
            className="details-btn"
            onClick={() => onViewDetails(tx)}
            disabled={!hasInvoice}
            title={detailsTitle}
            ariaLabel={detailsTitle}
            stopPropagation
          />
        );
      },
    }),
    [t, onViewDetails, hidden],
  );

  return (
    <DataTable
      columns={TRANSACTIONS_COLUMNS}
      renderers={renderers}
      rows={transactions}
      rowKey={(tx) => tx.id}
      minWidth={TRANSACTIONS_TABLE_MIN_WIDTH}
      pagination={{ total, page, rowsPerPage, onPageChange, onRowsPerPageChange }}
    />
  );
});

export default TransactionsTable;
